import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Avatar, TextField,
  Skeleton, Alert, Chip, IconButton,
} from '@mui/material';
import { Coffee, Edit, Send, ArrowBack } from '@mui/icons-material';
import groupsService from '../../services/groupsService';
import { colors, designTokens } from '../../theme/theme';

interface BoardAnswer {
  user_id: string;
  first_name: string;
  avatar_url: string | null;
  answer: string;
  is_mine: boolean;
  created_at: string | null;
}

interface BoardQuestion {
  question_id: string;
  question_text: string;
  answers: BoardAnswer[];
  my_answer: string | null;
}

export default function GroupBoardPage() {
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<BoardQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadBoard = useCallback(async () => {
    if (!groupId) return;
    try {
      const data = await groupsService.getBoard(groupId);
      setQuestions(data.questions);
      setError('');
    } catch (e: any) {
      console.warn('[GroupBoardPage] load board failed', e);
      setError(e.message || 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // Poll every 30 seconds for new answers
  useEffect(() => {
    const interval = setInterval(loadBoard, 30000);
    return () => clearInterval(interval);
  }, [loadBoard]);

  const handleSubmit = async (questionId: string) => {
    if (!groupId || !answerText.trim()) return;
    setSubmitting(true);
    try {
      await groupsService.postAnswer(groupId, questionId, answerText.trim());
      setEditingQuestion(null);
      setAnswerText('');
      await loadBoard();
    } catch (e: any) {
      console.warn('[GroupBoardPage] post answer failed', e);
      setError(e.message || 'Failed to post answer');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (questionId: string, currentAnswer?: string) => {
    setEditingQuestion(questionId);
    setAnswerText(currentAnswer || '');
  };

  if (loading) return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 2 }} />
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 2 }} />
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton aria-label="Go back" onClick={() => navigate(-1)} size="small"><ArrowBack /></IconButton>
        <Coffee sx={{ color: colors.primary.main }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Group Board
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
        Break the ice before you meet! Answer the questions below so your tablemates can get to know you.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Questions */}
      {questions.map((q) => (
        <Paper key={q.question_id} sx={{
          p: 3, mb: 2.5, borderRadius: 3,
          border: `1px solid ${designTokens.glass.border}`,
          bgcolor: colors.background.surface,
        }}>
          {/* Question */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: colors.primary.dark }}>
            {q.question_text}
          </Typography>

          {/* Existing answers */}
          {q.answers.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {q.answers.map((a, i) => (
                <Box key={i} sx={{
                  display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  bgcolor: a.is_mine ? colors.primary.subtle : colors.neutral[50],
                  p: 1.5, borderRadius: 2,
                }}>
                  <Avatar src={a.avatar_url || undefined} sx={{ width: 32, height: 32, bgcolor: colors.primary.light, fontSize: 14 }}>
                    {a.first_name?.[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: colors.neutral[600] }}>
                      {a.first_name} {a.is_mine && <Chip label="You" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', bgcolor: colors.primary.main, color: 'white' }} />}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.3 }}>{a.answer}</Typography>
                  </Box>
                  {a.is_mine && editingQuestion !== q.question_id && (
                    <IconButton size="small" aria-label="Edit my answer" onClick={() => startEditing(q.question_id, a.answer)}>
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.neutral[400], fontStyle: 'italic', mb: 2 }}>
              No answers yet — be the first!
            </Typography>
          )}

          {/* Answer input */}
          {(q.my_answer === null || editingQuestion === q.question_id) && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth size="small" multiline maxRows={3}
                placeholder="Your answer..."
                value={editingQuestion === q.question_id ? answerText : ''}
                onChange={(e) => { setAnswerText(e.target.value.slice(0, 200)); if (editingQuestion !== q.question_id) startEditing(q.question_id); }}
                onFocus={() => { if (editingQuestion !== q.question_id) startEditing(q.question_id, q.my_answer || ''); }}
                helperText={editingQuestion === q.question_id ? `${answerText.length}/200` : ''}
                inputProps={{ maxLength: 200 }}
              />
              <IconButton
                color="primary" aria-label="Submit answer" disabled={submitting || !answerText.trim()}
                onClick={() => handleSubmit(q.question_id)}
                sx={{ bgcolor: colors.primary.main, color: 'white', '&:hover': { bgcolor: colors.primary.dark }, '&.Mui-disabled': { bgcolor: colors.neutral[200] } }}>
                <Send sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Paper>
      ))}

      {/* Encouragement */}
      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: colors.primary.subtle, borderRadius: 3 }}>
        <Typography variant="body2" sx={{ color: colors.primary.dark }}>
          Your answers help make the first few minutes less awkward. No pressure — keep it light and fun!
        </Typography>
      </Paper>
    </Container>
  );
}
